import { api, mutate, mutateUpload, readCache, SIGNED_OUT_MESSAGE, UPLOAD_TIMEOUT_MS, type Ack } from './api'
import { announce, getDraft, markChoosing, putDraft, removeDraft, type MediaState } from './media-store'
import { getMe } from './me'
import type { MediaItem } from './types'
import { dayLabel, el, toast } from './ui'
import { roundDay, shownClock } from './round-config'

// Photos (and the voice-note audio) on a visit — step 2. attachPhotos turns
// a container into a photo strip: an optional **Photo** button (behind it a
// hidden <input type="file" capture>, so the PHONE opens the camera), a tiny
// label picker (Before / After / Problem / Additional notes / Skip), and the thumbnails that
// come back from GET /api/cleans/:id/photos. Voice clips arrive in the same
// list and render as small players. Tap a thumbnail for the full-size
// overlay — label, time, who took it, and (owners only) Delete.
//
// Same offline choice as the voice note: the photo is NOT in the write queue
// (a Blob cannot sit in localStorage). A failed upload keeps it with Retry /
// Discard, and since 2026-09-24 it is also kept ON THE PHONE (media-store.ts,
// IndexedDB) from the moment it is taken until it is sent or discarded, so a
// phone that puts the page away in a dead zone no longer loses it.

// What a card says about a photo it found kept on the phone.
const NOT_SENT = 'Photo not sent yet. Tap to send'

const MAX_EDGE = 1600
const JPEG_QUALITY = 0.82
// A hair past the upload fetch's own timeout, so a lost ANSWER still unpins
// the button (the voice note's watchdog, same reasoning).
const SEND_WATCHDOG_MS = UPLOAD_TIMEOUT_MS + 5_000

// The server stamps UTC; the card's other clocks read the round's clock (round-config.ts,
// Step 1), so this one does too (a summer photo read an hour earlier than its blocked stamp).
function localClock(at: string): string {
  const d = new Date(`${at.slice(0, 10)}T${at.slice(11, 19)}Z`)
  if (Number.isNaN(d.getTime())) return at.slice(11, 16)
  return shownClock(d)
}

const LABEL_WORDS: Record<string, string> = {
  before: 'Before',
  after: 'After',
  problem: 'Problem',
  // Details for a new job (0069; Liam, 13 Oct 2026).
  notes: 'Additional notes',
}

// A photo taken but not yet uploaded used to live only inside the mounted
// card — a date switch or a data refresh re-rendered the card and silently
// threw the shot away. Drafts now live here, keyed by visit id (in memory
// only — a Blob cannot sit in localStorage), so any later card for the SAME
// visit restores the waiting picker or the retry — bytes, label and
// Idempotency-Key included.
type Draft = { blob: Blob; width: number; height: number; key: string; label: string | null }
// 'sending' too (review 2026-09-24): a card drawn again while its photo is on
// its way shows "Sending…", never the label picker, so the shot cannot be
// labelled and sent a second time.
const pendingDrafts = new Map<number, { draft: Draft; state: 'choosing' | 'sending' | 'retry'; error: string }>()

// Photos chosen but still being turned into a JPEG (decode + canvas): not a
// draft yet, but the only copy of the shot is already in this page's memory.
let preparing = 0

// True while any visit holds an unsent photo, or one is still being
// prepared. The Today screen checks it before the day strip moves to another
// date — switching dates re-renders the cards, and the user should know a
// photo is still waiting.
export function hasPendingPhotoDrafts(): boolean {
  return pendingDrafts.size > 0 || preparing > 0
}

// Signed in as another account now (me.ts announces it, and reloads the page
// unless someone objects). A photo not yet sent exists only in this page's
// memory, so while any is waiting the reload is refused, and every card
// holding one for retry says what to do instead. Nothing is sent from these
// screens meanwhile (api.ts); the photo goes once the first account is back.
const ACCOUNT_CHANGED =
  "You're signed in as another account now. Sign back in as the first one to send this photo, or discard it."
// Each card holding a photo for retry: (words to show, only if it shows these).
const cardsHoldingRetry = new Set<(words: string, onlyIf?: string) => void>()

window.addEventListener('dropby:identity-changed', (e) => {
  if (!hasPendingPhotoDrafts()) return
  e.preventDefault()
  for (const [id, p] of pendingDrafts) if (p.state === 'retry') pendingDrafts.set(id, { ...p, error: ACCOUNT_CHANGED })
  for (const tell of [...cardsHoldingRetry]) tell(ACCOUNT_CHANGED)
})

// Only what this account can do changed (several jobs switched): the photo is still this account's and still sends;
// the page's redraw just waits for it (me.ts).
window.addEventListener('dropby:capability-changed', (e) => {
  if (hasPendingPhotoDrafts()) e.preventDefault()
})

// What happened to a job's photo, from any card or from the background send
// (media-store.ts announce): the copy kept in memory follows it here, and each
// card for that job follows it in attachPhotos.
window.addEventListener('roundly:media-state', (e) => {
  const d = (e as CustomEvent<MediaState>).detail
  if (!d || d.kind !== 'photo') return
  const p = pendingDrafts.get(d.cleanId)
  if (d.what === 'sent' || d.what === 'cleared') pendingDrafts.delete(d.cleanId)
  else if (p && d.what === 'sending') pendingDrafts.set(d.cleanId, { ...p, state: 'sending' })
  else if (p && d.what === 'unsent') pendingDrafts.set(d.cleanId, { ...p, state: 'retry', error: d.error || NOT_SENT })
})

// The first account is back (me.ts): the waiting photos can go again.
window.addEventListener('dropby:identity-restored', () => {
  for (const [id, p] of pendingDrafts) if (p.error === ACCOUNT_CHANGED) pendingDrafts.set(id, { ...p, error: 'Retry' })
  for (const tell of [...cardsHoldingRetry]) tell('Retry', ACCOUNT_CHANGED)
})

export interface AttachPhotosOptions {
  clean: { id: number }
  // When given, the Photo button is rendered here (a card's .clean-actions
  // row) instead of above the strip — so on a Today card the button joins
  // Done · Couldn't do it · Voice note. No buttonHost = strip only.
  buttonHost?: HTMLElement
  // Diary rows: 48px thumbnails (the cards use the roomier default).
  small?: boolean
  // The finish panel's feedback box (2026-09-23): its own button words, and a
  // state callback so the panel can wait for a photo that is still sending.
  idleLabel?: string
  onStateChange?: (state: 'idle' | 'choosing' | 'sending' | 'retry') => void
}

type State = 'idle' | 'choosing' | 'sending' | 'retry'

export function attachPhotos(container: HTMLElement, opts: AttachPhotosOptions): void {
  const cleanId = opts.clean.id
  const me = {} // this card, so it can ignore what it announced itself
  let items: MediaItem[] = []
  let state: State = 'idle'
  // The photo awaiting its label, or kept for Retry: one Idempotency-Key per
  // photo intent, so a retry re-sends byte-identical bytes. The chosen label
  // rides with the draft — a retry must not silently drop it. The SAME object
  // is referenced by pendingDrafts (below), so key/label changes stay in step.
  let draft: Draft | null = null
  let lastError = ''
  let discardBtn: HTMLButtonElement | null = null
  let watchdog = 0

  const canDelete = getMe()?.role === 'owner'

  // ---- strip ---------------------------------------------------------------

  const strip = el('div', { class: `photo-strip${opts.small ? ' photo-strip-small' : ''}` })
  const fileInput = el('input', {
    type: 'file',
    accept: 'image/*',
    capture: 'environment',
    class: 'photo-file-input',
    'aria-hidden': 'true',
    tabindex: '-1',
  }) as HTMLInputElement

  function renderStrip(): void {
    while (strip.firstChild) strip.removeChild(strip.firstChild)
    strip.hidden = items.length === 0
    for (const m of items) {
      if (m.kind === 'audio') {
        const audio = el('audio', { controls: '', preload: 'none', src: `/api/media/${m.id}` })
        audio.setAttribute('aria-label', 'Voice note on this visit')
        strip.append(el('span', { class: 'photo-audio' }, audio))
      } else {
        const img = el('img', {
          loading: 'lazy',
          src: `/api/media/${m.id}`,
          alt: m.label ? LABEL_WORDS[m.label] ?? 'Photo' : 'Photo',
        })
        const thumb = el('button', { type: 'button', class: 'photo-thumb' }, img)
        thumb.addEventListener('click', () => openOverlay(m))
        strip.append(thumb)
      }
    }
  }

  // ---- the label picker ------------------------------------------------------

  const picker = el('div', { class: 'photo-pick', hidden: '' })
  function closePicker(): void {
    markChoosing(cleanId, false)
    picker.hidden = true
    while (picker.firstChild) picker.removeChild(picker.firstChild)
    if (state === 'choosing') state = 'idle'
  }
  function openPicker(): void {
    while (picker.firstChild) picker.removeChild(picker.firstChild)
    picker.append(el('p', { class: 'photo-pick-title' }, 'Label this photo'))
    for (const [value, word] of Object.entries(LABEL_WORDS)) {
      const b = el('button', { type: 'button', class: 'photo-pick-btn' }, word)
      b.addEventListener('click', () => void startUpload(value))
      picker.append(b)
    }
    const skip = el('button', { type: 'button', class: 'photo-pick-btn photo-pick-skip' }, 'Skip')
    skip.addEventListener('click', () => void startUpload(null))
    picker.append(skip)
    picker.hidden = false
    state = 'choosing'
    markChoosing(cleanId, true)
  }

  // ---- upload ----------------------------------------------------------------

  // This card's answer to an account change (or return) while it holds a
  // photo for retry.
  const tellAccountChanged = (words: string, onlyIf?: string): void => {
    if (buttonRef && !buttonRef.isConnected) cardsHoldingRetry.delete(tellAccountChanged)
    else if (state === 'retry' && (onlyIf === undefined || lastError === onlyIf)) keepForRetry(words, buttonRef)
  }

  function setButton(button: HTMLButtonElement | null): void {
    opts.onStateChange?.(state)
    if (state === 'retry') cardsHoldingRetry.add(tellAccountChanged)
    else cardsHoldingRetry.delete(tellAccountChanged)
    if (!button) return
    button.classList.toggle('voice-error', state === 'retry')
    button.disabled = state === 'sending'
    if (state === 'sending') button.textContent = 'Sending…'
    else if (state === 'retry') button.textContent = lastError || 'Retry'
    else button.textContent = opts.idleLabel ?? 'Photo'
    if (state === 'retry' && !discardBtn) {
      discardBtn = el('button', { type: 'button', class: 'photo-discard' }, 'Discard')
      discardBtn.addEventListener('click', () => {
        draft = null
        pendingDrafts.delete(cleanId)
        void removeDraft('photo', cleanId)
        announce({ kind: 'photo', cleanId, what: 'cleared', from: me })
        state = 'idle'
        lastError = ''
        if (discardBtn) {
          discardBtn.remove()
          discardBtn = null
        }
        setButton(button)
      })
      button.after(discardBtn)
    }
    if (state !== 'retry' && discardBtn) {
      discardBtn.remove()
      discardBtn = null
    }
  }

  // The phone resizes BEFORE upload (longest edge 1600px, JPEG q0.82) — there
  // is deliberately no server-side resizer, and no second "thumbnail" upload:
  // one image per photo, and the gallery just sizes it with CSS. A file the
  // browser cannot decode (HEIC on an old browser; a corrupt file) is refused
  // here with the reason rather than uploaded for the server to bounce.
  async function resizeToJpeg(file: File): Promise<{ blob: Blob; width: number; height: number } | null> {
    if (typeof createImageBitmap !== 'function') return null
    let bitmap: ImageBitmap
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      try {
        bitmap = await createImageBitmap(file)
      } catch {
        return null
      }
    }
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const cx = canvas.getContext('2d')
    if (!cx) return null
    cx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
    return blob ? { blob, width, height } : null
  }

  async function startUpload(label: string | null): Promise<void> {
    const button = buttonRef
    if (!draft) return
    draft.label = label ?? draft.label
    const sending = draft
    markChoosing(cleanId, false)
    void putDraft({ kind: 'photo', cleanId, blob: sending.blob, key: sending.key, label: sending.label, width: sending.width, height: sending.height, ready: true })
    picker.hidden = true
    state = 'sending'
    pendingDrafts.set(cleanId, { draft: sending, state: 'sending', error: '' })
    announce({ kind: 'photo', cleanId, what: 'sending', from: me })
    setButton(button)
    if (watchdog) clearInterval(watchdog)
    const startedAt = Date.now()
    watchdog = window.setInterval(() => {
      if (state !== 'sending') clearInterval(watchdog)
      else if (Date.now() - startedAt > SEND_WATCHDOG_MS) {
        clearInterval(watchdog)
        failed('That never came back — retry?', button)
      }
    }, 1000)
    let ack: Ack<MediaItem>
    try {
      ack = await mutateUpload<MediaItem>({
        path: `/api/cleans/${cleanId}/photos`,
        key: sending.key,
        fields: {
          ...(sending.label ? { label: sending.label } : {}),
          ...(sending.width ? { width: String(sending.width) } : {}),
          ...(sending.height ? { height: String(sending.height) } : {}),
        },
        file: sending.blob,
        filename: 'photo.jpg',
        fileField: 'image',
        ackField: 'media',
      })
    } catch {
      ack = { ok: false, status: 0, error: 'no connection' }
    }
    clearInterval(watchdog)
    watchdog = 0
    if (state !== 'sending') return // the watchdog already released the button
    if (ack.ok && ack.row) {
      draft = null
      pendingDrafts.delete(cleanId) // confirmed saved — nothing is waiting
      void removeDraft('photo', cleanId)
      state = 'idle'
      setButton(button)
      // A retry that the server replayed answers with the SAME media row —
      // do not show the photo twice.
      const saved = ack.row as { id?: number }
      if (!items.some((m) => m.id === saved.id)) items = [...items, ack.row]
      renderStrip()
      announce({ kind: 'photo', cleanId, what: 'sent', row: ack.row, from: me })
      toast('Photo added')
      return
    }
    if (ack.signedOut) {
      window.alert(SIGNED_OUT_MESSAGE)
      failed('Sign in, then retry', button)
      return
    }
    if (ack.code === 'business_mismatch') {
      // Signed in as another account now: this photo is the first one's.
      // Nothing was written, so it keeps its key; it goes once that account
      // is back (and the page does not reload over it meanwhile).
      failed(ACCOUNT_CHANGED, button)
      return
    }
    if (ack.status === 0) {
      failed('No signal — try again when you have signal', button)
      return
    }
    if (ack.status === 409) {
      // This key is burned (reused with different bytes, or bound to another
      // record). The photo survives; Retry gets a FRESH key, so the re-send
      // is a brand-new intent instead of colliding forever.
      draft.key = crypto.randomUUID()
      void putDraft({ kind: 'photo', cleanId, blob: draft.blob, key: draft.key, label: draft.label, width: draft.width, height: draft.height, ready: true })
      failed('Send refused — retry starts it fresh', button)
      return
    }
    if (ack.status >= 500 || (ack.status >= 200 && ack.status < 300)) {
      // Server trouble, or a success answer that never made sense. Nothing is
      // wrong with the photo: the draft and the SAME key survive — the retry
      // re-sends byte-identical bytes, which the server replays safely even
      // if the first attempt actually committed.
      failed(ack.error || 'The server could not save it — retry?', button)
      return
    }
    // 400 (too large / bad format), 401/403 (not allowed): retrying cannot help.
    window.alert(ack.error ?? 'The photo was not saved')
    draft = null
    pendingDrafts.delete(cleanId)
    void removeDraft('photo', cleanId)
    announce({ kind: 'photo', cleanId, what: 'cleared', from: me })
    state = 'idle'
    setButton(button)
  }

  function keepForRetry(error: string, button: HTMLButtonElement | null): void {
    lastError = error
    state = 'retry'
    if (draft) pendingDrafts.set(cleanId, { draft, state: 'retry', error })
    setButton(button)
  }

  // This card's own send failed: kept for Retry, and every other card for the
  // job is told so it stops showing "Sending…".
  function failed(error: string, button: HTMLButtonElement | null): void {
    keepForRetry(error, button)
    announce({ kind: 'photo', cleanId, what: 'unsent', error, from: me })
  }

  // ---- the Photo button --------------------------------------------------------

  const buttonRef = opts.buttonHost
    ? el('button', { type: 'button', class: 'photo-btn ic ic-camera' }, opts.idleLabel ?? 'Photo')
    : null
  if (buttonRef) {
    buttonRef.addEventListener('click', () => {
      if (state !== 'idle') {
        if (state === 'retry') void startUpload(draft?.label ?? null) // retry keeps the label already picked
        return
      }
      fileInput.click()
    })
    opts.buttonHost!.append(buttonRef)
  }
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0]
    fileInput.value = '' // the same photo may be picked again after a failure
    if (!file || state !== 'idle') return
    // Held from the moment it is chosen: a switch of account during the
    // resize must not reload the page over it.
    preparing++
    let resized: Awaited<ReturnType<typeof resizeToJpeg>>
    try {
      resized = await resizeToJpeg(file)
    } finally {
      preparing--
    }
    if (!resized) {
      window.alert(
        "This photo's format could not be read on this phone — take it again from the Photo button (the app saves photos as JPEG).",
      )
      return
    }
    draft = { ...resized, key: crypto.randomUUID(), label: null }
    pendingDrafts.set(cleanId, { draft, state: 'choosing', error: '' })
    void putDraft({ kind: 'photo', cleanId, blob: draft.blob, key: draft.key, label: null, width: draft.width, height: draft.height, ready: false })
    openPicker()
  })

  // A draft left by an earlier card for THIS visit comes back: a retry shows
  // its error again (same Idempotency-Key), a taken-but-unlabelled photo
  // re-opens the label picker. Without this, switching dates or refreshing
  // the data silently threw the waiting shot away.
  const pending = pendingDrafts.get(cleanId)
  if (pending) {
    draft = pending.draft
    if (pending.state === 'retry') keepForRetry(pending.error || 'Retry', buttonRef)
    else if (pending.state === 'sending') {
      // On its way from a card drawn earlier: this one waits to hear how it went.
      state = 'sending'
      setButton(buttonRef)
    } else openPicker()
  } else {
    // Nothing in memory: a photo kept on the phone from an earlier page (the
    // phone put the page away before it was sent) comes back as a Retry, with
    // its label and its Idempotency-Key.
    void getDraft('photo', cleanId).then((kept) => {
      if (!kept || draft || state !== 'idle' || pendingDrafts.has(cleanId)) return
      draft = { blob: kept.blob, width: kept.width ?? 0, height: kept.height ?? 0, key: kept.key, label: kept.label ?? null }
      keepForRetry(NOT_SENT, buttonRef)
    })
  }

  // What another card, or the background send, did with this job's photo.
  const onState = (e: Event): void => {
    const d = (e as CustomEvent<MediaState>).detail
    if (!d || d.kind !== 'photo' || d.cleanId !== cleanId || d.from === me) return
    if (!container.isConnected) {
      window.removeEventListener('roundly:media-state', onState)
      return
    }
    if (d.what === 'sending') {
      if (state === 'retry' || state === 'choosing') {
        if (state === 'choosing') closePicker()
        state = 'sending'
        setButton(buttonRef)
      }
      return
    }
    if (d.what === 'unsent') {
      if (state !== 'sending') return
      const p = pendingDrafts.get(cleanId)
      if (p) draft = p.draft
      if (draft) keepForRetry(d.error || NOT_SENT, buttonRef)
      else {
        state = 'idle'
        setButton(buttonRef)
      }
      return
    }
    // sent, or thrown away / refused for good: nothing waits on this card now.
    if (state === 'choosing') closePicker()
    draft = null
    state = 'idle'
    lastError = ''
    setButton(buttonRef)
    const saved = d.what === 'sent' ? (d.row as MediaItem | null) : null
    if (saved && typeof (saved as { id?: unknown }).id === 'number' && !items.some((m) => m.id === saved.id)) {
      items = [...items, saved]
      renderStrip()
    }
  }
  window.addEventListener('roundly:media-state', onState)

  // ---- full-size overlay ---------------------------------------------------------

  function openOverlay(m: MediaItem): void {
    const existing = document.querySelector('.photo-overlay')
    if (existing) existing.remove()
    const close = (): void => overlay.remove()
    const overlay = el('div', { class: 'photo-overlay', role: 'dialog', 'aria-label': 'Photo' })
    const inner = el('div', { class: 'photo-overlay-inner' })
    if (m.kind === 'audio') {
      const audio = el('audio', { controls: '', src: `/api/media/${m.id}` })
      inner.append(audio)
    } else {
      inner.append(el('img', { src: `/api/media/${m.id}`, alt: m.label ? LABEL_WORDS[m.label] ?? 'Photo' : 'Photo' }))
    }
    // The round's day as well as its clock (a 23:30 UTC photo in summer is the next day in the UK).
    const stamp = new Date(`${m.created_at.slice(0, 10)}T${m.created_at.slice(11, 19)}Z`)
    const when = `${dayLabel(Number.isNaN(stamp.getTime()) ? m.created_at.slice(0, 10) : roundDay(stamp))}, ${localClock(m.created_at)}`
    const caption = el(
      'p',
      { class: 'photo-overlay-caption' },
      // Who took it is for the owner; a worker is not shown other people's
      // email addresses (review 2026-09-24).
      `${m.kind === 'audio' ? 'Voice note' : m.label ? LABEL_WORDS[m.label] ?? 'Photo' : 'Photo'} · ${when}${canDelete ? ` · ${m.created_by}` : ''}`,
    )
    inner.append(caption)

    if (canDelete) {
      const del = el('button', { type: 'button', class: 'btn-danger' }, 'Delete')
      del.addEventListener('click', () => {
        // Inline confirm, not window.confirm — the app's other destructive
        // actions ask inside the page.
        const ask = el('span', { class: 'photo-delete-ask' })
        const yes = el('button', { type: 'button', class: 'btn-danger' }, 'Delete photo')
        const no = el('button', { type: 'button' }, 'Keep')
        yes.addEventListener('click', () => void doDelete())
        no.addEventListener('click', () => ask.remove())
        ask.append(el('span', {}, 'Delete it? '), yes, no)
        del.replaceWith(ask)
      })
      const doDelete = async (): Promise<void> => {
        del.disabled = true
        const ack = await mutate({
          label: 'Delete photo',
          method: 'DELETE',
          path: `/api/media/${m.id}`,
          body: {},
          onConflict: close,
        })
        if (ack.ok) {
          items = items.filter((x) => x.id !== m.id)
          renderStrip()
          close()
          toast('Deleted')
          return
        }
        del.disabled = false
        if (ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
      }
      inner.append(del)
    }

    const closeBtn = el('button', { type: 'button', class: 'photo-overlay-close', 'aria-label': 'Close' }, '×')
    closeBtn.addEventListener('click', close)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close()
    })
    overlay.append(closeBtn, inner)
    document.body.append(overlay)
  }

  // ---- load -----------------------------------------------------------------

  async function load(): Promise<void> {
    try {
      const data = await api<{ media: MediaItem[] }>(`/api/cleans/${cleanId}/photos`, `photos:${cleanId}`)
      items = data.media ?? []
    } catch {
      // Offline: show this phone's last copy of the strip, quietly. A photo
      // strip is never worth an error bar over the visit it sits on.
      const cached = readCache<{ media: MediaItem[] }>(`photos:${cleanId}`)
      items = cached?.data.media ?? []
    }
    renderStrip()
  }
  void load()

  container.append(fileInput, picker, strip)
}
